import styled, { css } from "styled-components";
import { Flex, shadows, transition } from "@evergis/ui";

import { ContainerWrapper, PaginationWrapper } from "../FeatureCard/styled";
import { LayerGroupList, LayerListContainer } from "../LayerList/styled";
import { PROJECT_PANEL_HEADER_HEIGHT } from "../ProjectPanel/constants";
import { LEFT_PANEL_MIN_WIDTH } from "../../components/Layout/constants";

import { images } from "./assets";

export const DashboardPlaceholderWrap = styled(Flex)`
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 2rem;
`;

export const DashboardPlaceholder = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &&& > * {
    margin-bottom: 0;
  }

  > div {
    width: 100%;
    margin-top: 1rem;
    font-size: 0.75rem;
    text-align: center;
    color: ${({ theme: { palette } }) => palette.textDisabled};
  }

  span[kind] {
    width: 2.25rem;
    height: 2.25rem;
    opacity: 0.28;

    :after {
      font-size: 32px;
    }
  }
`;

export const DashboardWrapper = styled(Flex)<{ hasImage?: boolean; isPresentationMode?: boolean }>`
  flex-direction: column;
  flex-wrap: nowrap;
  flex-grow: 1;
  width: calc(100% - 3rem);
  height: calc(100% - ${PROJECT_PANEL_HEADER_HEIGHT});

  > ${ContainerWrapper} {
    > * {
      margin-bottom: 2rem;
    }
  }

  ${({ hasImage, isPresentationMode }) =>
    hasImage &&
    !isPresentationMode &&
    css`
      margin-top: -0.35rem;
    `}
`;

export const DashboardContent = styled(Flex)`
  flex-grow: 1;
  width: 100%;
  padding: 1.5rem 1.5rem 2rem;
  overflow-y: auto;
  scrollbar-gutter: stable;
`;

const PresentationWrapperCss = css`
  margin-bottom: 0.75rem;
  padding: 1.5rem;
  background-color: ${({ theme: { palette } }) => palette.panelBackground};
  backdrop-filter: blur(10px);
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  box-shadow: ${shadows.raised};
`;

export const PresentationWrapper = styled.div`
  ${PresentationWrapperCss};
  position: relative;
  z-index: 1;
`;

export const PresentationPanelWrapper = styled(PresentationWrapper)`
  margin-top: 0.75rem;
  transition: background-color ${transition.toggle};
`;

export const PresentationHeader = styled.div<{ hasImage?: boolean }>`
  margin: -1.5rem -1.5rem 0 -1.5rem;
  padding: 1.5rem;
  background: url(${images.presentationHeader}) 0 0 no-repeat;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  transition: background-color ${transition.toggle};

  ${({ hasImage }) =>
    hasImage &&
    css`
      padding-top: 7rem;
    `};
`;

export const PresentationHeaderIcon = styled.img<{ onClick?: VoidFunction }>`
  &&& {
    position: absolute;
    top: 1.5rem;
    left: 1.5rem;
  }

  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  max-height: 2.5rem;
`;

export const PresentationHeaderTools = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: -0.5rem;
  margin-right: -0.5rem;

  span[kind="filter_switcher"],
  span[kind="sun"],
  span[kind="moon"] {
    :after {
      font-size: 0.85rem;
      color: ${({ theme: { palette } }) => palette.iconDisabled};
    }

    :hover:after {
      color: ${({ theme: { palette } }) => palette.icon};
    }
  }
`;

export const PresentationHeaderButtons = styled(Flex)``;

export const PresentationPanelContainer = styled.div<{ left?: number; showLayers?: boolean }>`
  position: absolute;
  top: 0;
  left: calc(${({ left }) => left || 0}px + 0.75rem);
  bottom: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  width: ${LEFT_PANEL_MIN_WIDTH}px;
  padding-right: 0.5rem;
  scrollbar-gutter: stable;
  overflow-y: hidden;

  :hover {
    overflow-y: auto;
  }

  ${({ showLayers }) =>
    !showLayers &&
    css`
      > :first-child {
        padding-bottom: 0;
      }
    `};

  ${PresentationHeader} > * {
    position: relative;
    z-index: 2;
  }

  ${PaginationWrapper} {
    ${PresentationWrapperCss};
    width: calc(100% - 2rem);
    min-height: 8.625rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    margin-bottom: 0.75rem;
  }

  ${DashboardWrapper} {
    width: 100%;
    margin-top: 0;

    ${DashboardContent} {
      padding: 0;
      overflow-y: unset;

      > * > ${ContainerWrapper} > * > ${ContainerWrapper} {
        ${PresentationWrapperCss};
        width: calc(100% - 3rem);
      }
    }
  }

  ${DashboardPlaceholder} {
    ${PresentationWrapperCss};
    width: 18.5rem;
    height: 10.75rem;
  }

  ${LayerListContainer} {
    height: auto;
    margin: 0 -1.125rem -1.5rem;
    padding: 0;

    ${LayerGroupList} {
      padding: 0;
    }
  }
`;
